import { SectionConfig, SectionNotes } from '../services/api/vehicle-sections/types';
import { User } from './user';

export type VehicleOption = {
  id: number;
  vehicle_id: number;
  bluebook_option_id: number;
  option_name: string;
  is_typical: boolean;
};

export type VehicleSectionGrade = {
  id: number;
  slug: string;
  color: string;
  label: string;
};

export type VehicleSectionImage = {
  id: number;
  url: string;
  url_thumb: string;
  label?: string;
  notes?: string;
  is_damaged?: boolean;
  type: string | 'user_defined';
};

type Section = {
  id: number;
  name: keyof SectionConfig;
  sub_section_name: string;
  type: 'checkbox' | 'block';
};

type GroupedSections = Record<keyof SectionConfig, VehicleSection[]>;

export type VehicleSectionCheckbox = {
  id: number;
  label: string;
  notes?: string;
  selected: boolean;
};

export type VehicleSection = {
  id: number;
  label: string;
  grade?: VehicleSectionGrade;
  notes?: string;
  images: VehicleSectionImage[];
  section: Section;
  audio_url?: string;
  checkboxes?: VehicleSectionCheckbox[];
};

type VehicleStatus = 'active' | 'pulled' | 'draft' | 'deactivated' | 'under_review' | 'awarded';

export type VehicleBanner = {
  id: number;
  label: string;
  is_visible: boolean;
  color: string;
};

export type AutoCheckInfo = {
  owner_count: number | null;
  has_accident: boolean | null;
  damage_instances: string[] | null;
  has_state_title_brand: boolean | null;
  state_title_brand: string | null;
  accident_severity: string | null;
  title_brand_check: boolean | null;
  usage_type: string | null;
  has_odometer_issue: boolean | null;
  has_mileage_inconsistency: boolean | null;
  last_odometer_value: number | null;
  last_odometer_check_date: string | null;
};

export type MoneyFlow = {
  id: number;
  vehicle_id: number;
  amount: number;
  date_received: string;
  payment_method: string;
};

export type Vehicle = {
  id: number;
  vin: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  status: VehicleStatus;
  transmission: string;
  ext_color: string;
  engine: string;
  drive_train: string;
  options: VehicleOption[];
  mileage: number;
  ai_mileage?: number;
  bluebook_auction_value_good: number;
  bluebook_mileage_adjustment: number;
  bluebook_mileage: number;
  bluebook_value: number;
  vehicleSections: VehicleSection[];
  is_guaranteed: boolean;
  upload_type: string;
  vin_code_url: string;
  groupedSections: GroupedSections;
  user: User;
  lister?: User;
  auction_type?: string;
  buyer_id?: number;
  admin_notes: string;
  inspector_notes: SectionNotes;
  salesperson_notes: string;
  banners: VehicleBanner[];
  autocheck_info: AutoCheckInfo;
  money_flow?: MoneyFlow[] | null;
  [x: string]: any;
};

export enum AuctionType {
  AUTOAXESS = 'autoaxess',
  FRONTLINE = 'frontline',
  SECOND_CHANCE = 'second_chance'
}

export type Auction = {
  id: number;
  location_id: number;
  type: AuctionType;
  status: string;
  date_end: string;
  slug: string;
};

export type AuctionGroup = {
  id: string;
  auctions: Auction[];
  created_at: string;
};

export enum OfferAuctionStatus {
  ENDED = 'ended',
  PRIVATE_BIDS = 'private_bids',
  PUBLIC_OFFERS = 'public_offers'
}

export enum OfferStatus {
  SENT = 'sent',
  SELLER_ACCEPTED = 'seller_accepted',
  ADMIN_ACCEPTED = 'admin_accepted',
  REJECTED = 'rejected',
  COUNTERED = 'countered',
  ADMIN_COUNTERED = 'admin_countered',
  PUBLIC = 'public'
}

export type Offer = {
  id: number;
  offer_auction_id: number;
  sender_id: number;
  sender: User;
  amount: number;
  notes: string;
  status: OfferStatus;
  created_at: string;
  is_admin: boolean;
  type: 'private' | 'public';
  is_negotiation_stopped: boolean;
  edited_at?: string;
  is_edited: boolean;
};

export type OfferAuctionBid = {
  id: number;
  amount: number;
  user_id: number;
  bidder: User;
  status: string;
  created_at: string;
  updated_at: string;
};

export type OfferAuction = {
  id: number; // same as vehicle_id
  status: OfferAuctionStatus;
  top_bidder_id: number;
  topBidder?: User;
  vehicle_id: number;
  privateOffers: Offer[];
  created_at: string;
  starting_bid: number;
  offerBids: OfferAuctionBid[];
  offerProxyBid?: OfferAuctionBid;
};
